<script setup>
import { ref, computed } from 'vue'
import { useUserStore } from '@/stores/user'

const userStore = useUserStore()

const hasEventAccess = computed(() => {
  return userStore.accountType >= 4 || userStore.accountType === 1337
})

const firstName = computed(() => {
  const n = (userStore.name || '').trim()
  if (!n) return 'there'
  return n.split(/\s+/)[0]
})

const greetingsByPeriod = {
  morning: [
    'Good morning, {name}',
    'Rise and shine, {name}',
    "Coffee's ready, {name}",
    'Fresh day, fresh logs, {name}',
    'Hope you brought coffee, {name}',
    "Up and at 'em, {name}",
    'Bright and early, {name}',
  ],
  lunch: [
    'Lunch break, {name}?',
    'Bon appétit, {name}',
    'Eating at your desk, {name}?',
    'Don\'t forget to eat, {name}',
    'Logs and lunch, {name}',
    'Munching alerts, {name}?',
    'Coffee refill, {name}?',
  ],
  afternoon: [
    'Good afternoon, {name}',
    'Powering through, {name}?',
    'Halfway there, {name}',
    'Post-lunch packets, {name}',
    'Round two, {name}?',
    'Afternoon grind, {name}',
  ],
  evening: [
    'Good evening, {name}',
    'Wrapping up, {name}?',
    'One more hunt, {name}?',
    'After-hours patrol, {name}',
    'Knocking off soon, {name}?',
    "Long day, {name}?",
    'Evening shift, {name}',
  ],
  night: [
    'Burning the midnight oil, {name}?',
    'Working late, {name}?',
    'Nocturnal hunt, {name}?',
    'Owl hours, {name}',
    'Still up, {name}?',
    'Late-night recon, {name}?',
    'Night shift, {name}',
  ],
  any: [
    'Back at it again, {name}',
    'Welcome back, {name}',
    'Ready to hunt, {name}?',
    'Stay frosty, {name}',
    "Let's dig in, {name}",
    'Logged in and locked on, {name}',
    'Eyes on the wire, {name}',
    'On the prowl, {name}?',
    'Make some noise, {name}',
    'Suspicion is a virtue, {name}',
    'What\'s on the wire, {name}?',
    'Hunt mode, {name}',
  ],
}

function currentPeriod() {
  const h = new Date().getHours()
  if (h < 5) return 'night'
  if (h < 12) return 'morning'      // 5–11
  if (h < 14) return 'lunch'        // 12–13
  if (h < 18) return 'afternoon'    // 14–17
  if (h < 22) return 'evening'      // 18–21
  return 'night'                    // 22–4
}

// Drop greetings that would render too long for the hero (avoids ugly wraps
// for users with long names).
const MAX_GREETING_CHARS = 38

// Pick once per page load so the greeting is stable while you're on it.
const greetingTemplate = ref((() => {
  const fullPool = [...greetingsByPeriod[currentPeriod()], ...greetingsByPeriod.any]
  const filtered = fullPool.filter(
    t => t.replace('{name}', firstName.value).length <= MAX_GREETING_CHARS
  )
  const pool = filtered.length ? filtered : fullPool
  return pool[Math.floor(Math.random() * pool.length)]
})())

const greetingBefore = computed(() => greetingTemplate.value.split('{name}')[0])
const greetingAfter = computed(() => greetingTemplate.value.split('{name}')[1] || '')

const primaryActions = computed(() => [
  {
    key: 'enrich',
    icon: ['fas', 'globe'],
    label: 'Enrich selectors',
    helper: 'Bulk-enrich IPs, domains, and other selectors with threat context.',
    to: '/enrich',
  },
  {
    key: 'search',
    icon: ['fas', 'magnifying-glass'],
    label: 'Search raw data',
    helper: 'Hunt across all raw events from your connected sources.',
    to: '/search/event',
    locked: !hasEventAccess.value,
  },
  {
    key: 'api',
    icon: ['fas', 'code'],
    label: 'Use the API',
    helper: 'Programmatically query, enrich, and ingest with the REST API.',
    to: '/docs/api',
  },
  {
    key: 'dashboards',
    icon: ['fas', 'table-cells-large'],
    label: 'Open dashboards',
    helper: 'Visualize queries and build live dashboards.',
    to: '/dashboard',
    locked: !hasEventAccess.value,
  },
  {
    key: 'signals',
    icon: ['fas', 'satellite-dish'],
    label: 'Browse signals',
    helper: 'Curated threat signals from across the Webscout network.',
    to: '/signals',
  },
])

</script>

<template>
  <div class="home-page">
    <div class="home-inner">
      <div class="home-main">
        <!-- Greeting hero -->
        <div class="hero">
          <h1 class="hero-greeting">
            {{ greetingBefore }}<span class="hero-name">{{ firstName }}</span>{{ greetingAfter }}
          </h1>
          <p class="hero-subhead">What would you like to do today?</p>
        </div>

        <!-- Primary actions -->
        <div class="primary-grid">
          <router-link
            v-for="action in primaryActions"
            :key="action.key"
            :to="action.to"
            class="primary-card"
          >
            <div class="primary-card-icon">
              <font-awesome-icon :icon="action.icon" />
            </div>
            <div class="primary-card-body">
              <div class="primary-card-title-row">
                <span class="primary-card-title">{{ action.label }}</span>
                <font-awesome-icon
                  v-if="action.locked"
                  :icon="['fas', 'lock']"
                  class="primary-card-lock"
                  title="Requires Elite or Enterprise"
                />
              </div>
              <p class="primary-card-helper">{{ action.helper }}</p>
            </div>
          </router-link>
        </div>
      </div>

      <!-- Brand footer -->
      <div class="hero-brand">
        <img src="~@/assets/ws_logo_idea.svg" class="hero-logo" alt="" />
        <img src="~@product/assets/logo_white.svg" class="hero-wordmark" alt="Webscout" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.home-page {
  width: 100%;
  height: 100%;
  background: var(--surface-page);
  overflow-y: auto;
}

.home-inner {
  max-width: 1080px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
}

.home-main {
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2.5rem;
}

/* Hero */
.hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1rem;
}

.hero-brand {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding-top: 1.5rem;
}

.hero-logo {
  height: 1.5rem;
  width: auto;
  opacity: 0.95;
}

.hero-wordmark {
  height: 1.25rem;
  width: auto;
  opacity: 0.95;
}

.hero-greeting {
  font-size: 2.75rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  letter-spacing: -0.02em;
  font-family: 'Jura', sans-serif;
  max-width: 100%;
  text-wrap: balance;
  line-height: 1.15;
}

.hero-name {
  color: var(--accent-text);
}

.hero-subhead {
  color: var(--text-muted);
  font-size: 1rem;
  margin: 0;
}

/* Primary action grid — flex-wrap so the trailing partial row centers */
.primary-grid {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
}

.primary-card {
  flex: 0 1 calc((100% - 1.5rem) / 3);
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  padding: 1rem 1.125rem;
  background: var(--surface-card);
  border: 1px solid transparent;
  border-radius: var(--radius-lg);
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  transition: background 0.15s ease, border-color 0.15s ease;
}

@media (max-width: 900px) {
  .primary-card {
    flex-basis: calc((100% - 0.75rem) / 2);
  }
}

@media (max-width: 600px) {
  .primary-card {
    flex-basis: 100%;
  }
}

.primary-card:hover {
  background: var(--surface-overlay-2);
}

.primary-card-icon {
  flex-shrink: 0;
  width: 2.25rem;
  height: 2.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--accent-soft-3);
  color: var(--accent-text);
  border-radius: var(--radius-md);
  font-size: 1rem;
}

.primary-card-body {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.primary-card-title-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.primary-card-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
}

.primary-card-lock {
  font-size: 0.7rem;
  color: var(--text-faint);
}

.primary-card-helper {
  font-size: 0.8rem;
  color: var(--text-muted);
  margin: 0;
  line-height: 1.4;
}

</style>
